import * as React from 'react';
import {Meteor} from 'meteor/meteor';
import {withTracker} from 'react-meteor-data-with-tracker';
import {
	AppBar,
	Box,
	Button,
	IconButton,
	LinkProps as LinkMuiProps,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Popover,
	Tab,
	TabProps,
	Tabs,
	Toolbar,
	Typography,
} from '@material-ui/core';
import {
	AccountBox as AccountBoxIcon,
	AccountCircle,
	ExitToApp as ExitToAppIcon,
} from '@material-ui/icons';
import {Link, LinkProps, NavLink, RouteComponentProps, withRouter} from 'react-router-dom';
import Icon from '@mdi/react';
import {mdiFacebook, mdiVk} from '@mdi/js';

import routerUrls from '../../constants/routerUrls';
import css from './UserHeader.pcss';
import {Group} from 'shared/collections/Groups';
import {sharedConsts} from 'shared/constants/sharedConsts';
import {methodNames} from 'shared/constants/methodNames';
import {PaidServicePackagesEnum} from 'shared/collections/PaidServices';
import LockIcon from 'client/components/common/icons/LockIcon';
import {LL} from 'shared/localization';
import {LangSwitcher} from 'shared/localization/LangSwitcher';

interface IUserHeaderProps {
	userLogged: boolean;
	defaultGroupId?: string;
}

interface IUserHeaderState {
	anchorEl: HTMLElement | null;
	isVideoHostingGranted: boolean;
	isSlideStreamsGranted: boolean;
}

interface ILinkTabProps extends TabProps {
	to?: string;
	component?: React.ReactNode;
}

export const LinkTab: React.FC<ILinkTabProps & (LinkProps | LinkMuiProps)> = (props) => (
	<Tab {...props} />
);

const LinkLabelWrapper: React.FC<{title: string; isGranted: boolean}> = ({title, isGranted}) => {
	if (isGranted) {
		return <span>{title}</span>;
	}

	return (
		<Box display="flex" alignItems="center">
			<span>{title}</span>
			<Box component="span" ml={0.5} mt={0.5}>
				<LockIcon
					viewBox="0 0 11 14"
					style={{width: '11px', height: '14px'}}
					fill="none"
					pathProps={{
						fill: 'white',
						fillOpacity: '0.54',
					}}
				/>
			</Box>
		</Box>
	);
};

class UserHeader extends React.PureComponent<
	IUserHeaderProps & RouteComponentProps,
	IUserHeaderState
> {
	state: IUserHeaderState = {
		anchorEl: null,
		isVideoHostingGranted: false,
		isSlideStreamsGranted: false,
	};

	componentDidMount() {
		Meteor.call(
			methodNames.paidServices.isGranted,
			[PaidServicePackagesEnum.VIDEO_HOSTING, PaidServicePackagesEnum.SLIDESHOW_STREAM],
			(error: Error | Meteor.Error, response: boolean) => {
				this.setState({
					isVideoHostingGranted: response[PaidServicePackagesEnum.VIDEO_HOSTING],
					isSlideStreamsGranted: response[PaidServicePackagesEnum.SLIDESHOW_STREAM],
				});
			},
		);
	}

	handleMenu = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
		this.setState({anchorEl: event.currentTarget});
	};

	handleClose = () => {
		this.setState({anchorEl: null});
	};

	handleLogout = () => {
		Meteor.logout();

		this.handleClose();
	};

	render() {
		const {userLogged, match, defaultGroupId} = this.props;
		const {anchorEl} = this.state;
		const open = Boolean(anchorEl);
		let activeMenu: string | null = null;

		if (match.path === routerUrls.userSlideshowGroup) {
			activeMenu = routerUrls.userSlideshowGroup;
		} else if (match.path.startsWith(routerUrls.userMockups)) {
			activeMenu = routerUrls.userMockups;
		} else if (match.path.startsWith(routerUrls.userAppsSets)) {
			activeMenu = routerUrls.userAppsSets;
		}

		const {isVideoHostingGranted, isSlideStreamsGranted} = this.state;

		return (
			<AppBar classes={{root: css.appHeader}} position="fixed">
				<div className="container">
					<Toolbar className={css.toolbar} variant="dense">
						<div className={css.menuAndHeader}>
							<Typography variant="h6" color="inherit" className={css.logo}>
								<NavLink to={routerUrls.home}>{LL('common.prtv')}</NavLink>
							</Typography>

							<Tabs className={css.tabs} variant="fullWidth" value={activeMenu}>
								{userLogged ? (
									<>
										<LinkTab
											classes={{root: css.linkTab}}
											value={routerUrls.userSlideshowGroup}
											key={routerUrls.userSlideshowGroup}
											to={routerUrls.userSlideshowGroup.replace(
												':groupId',
												defaultGroupId || '',
											)}
											component={Link}
											label={LL('common.userHeader.slideShow')}
										/>
										<LinkTab
											classes={{root: css.linkTab}}
											value={routerUrls.userMockups}
											key={routerUrls.userMockups}
											to={routerUrls.userMockups}
											component={Link}
											label={LL('common.userHeader.layouts')}
										/>
										<LinkTab
											classes={{root: css.linkTab}}
											value={routerUrls.userAppsSets}
											key={routerUrls.userAppsSets}
											to={routerUrls.userAppsSets}
											component={Link}
											label={LL('common.userHeader.collections')}
										/>
										<LinkTab
											classes={{root: css.linkTab}}
											value={routerUrls.userStreams}
											key={routerUrls.userStreams}
											to={
												isSlideStreamsGranted
													? routerUrls.userStreams
													: routerUrls.userPlan
											}
											component={Link}
											label={
												<LinkLabelWrapper
													title={LL('common.userHeader.streams')}
													isGranted={isSlideStreamsGranted}
												/>
											}
										/>
										<LinkTab
											classes={{root: css.linkTab}}
											value={routerUrls.userVideoHosting}
											key={routerUrls.userVideoHosting}
											to={
												isVideoHostingGranted
													? routerUrls.userVideoHosting
													: routerUrls.userPlan
											}
											component={Link}
											label={
												<LinkLabelWrapper
													title={LL('common.userHeader.videoHosting')}
													isGranted={isVideoHostingGranted}
												/>
											}
										/>
										<LinkTab
											classes={{root: css.linkTab}}
											value={routerUrls.extInformers}
											key={routerUrls.extInformers}
											href={routerUrls.extInformers}
											target="_blank"
											component="a"
											label={LL('common.userHeader.informers')}
										/>
										<LinkTab
											classes={{root: css.linkTab}}
											value={routerUrls.extInstructions}
											key={routerUrls.extInstructions}
											href={routerUrls.extInstructions}
											target="_blank"
											component="a"
											label={LL('common.userHeader.instructions')}
										/>
										<LinkTab
											classes={{root: css.linkTab}}
											key="supportUrl"
											href={`${LL('contacts.supportUrl')}`}
											target="_blank"
											component="a"
											label={LL('common.userHeader.technicalSupport')}
										/>
										<LinkTab
											classes={{root: css.linkTab}}
											key="forumUrl"
											href={`${LL('contacts.forumUrl')}`}
											target="_blank"
											component="a"
											label={LL('common.userHeader.forum')}
										/>
										<LinkTab
											classes={{root: css.linkTab}}
											value={routerUrls.contacts}
											key={routerUrls.contacts}
											to={routerUrls.contacts}
											component={Link}
											label={LL('common.userHeader.contacts')}
										/>
									</>
								) : (
									<>
										<LinkTab
											classes={{root: css.linkTab}}
											key={routerUrls.extInstructions}
											to={routerUrls.extInstructions}
											href={routerUrls.extInstructions}
											target="_blank"
											component="a"
											label={LL('common.userHeader.instructions')}
										/>

										<LinkTab
											classes={{root: css.linkTab}}
											value={routerUrls.contacts}
											key={routerUrls.contacts}
											to={routerUrls.contacts}
											component={Link}
											label={LL('common.userHeader.contacts')}
										/>

										<div style={{flex: 1}} />

										{/*<LinkTab*/}
										{/*	classes={{root: css.linkSocTab}}*/}
										{/*	to={routerUrls.extFacebook}*/}
										{/*	href={routerUrls.extFacebook}*/}
										{/*	target="_blank"*/}
										{/*	component="a"*/}
										{/*	label={*/}
										{/*		<Icon path={mdiFacebook} size={1} color="inherit" />*/}
										{/*	}*/}
										{/*/>*/}

										<LinkTab
											classes={{root: css.linkSocTab}}
											to={routerUrls.extVk}
											href={routerUrls.extVk}
											target="_blank"
											component="a"
											label={<Icon path={mdiVk} size={1} color="inherit" />}
										/>
									</>
								)}
							</Tabs>
						</div>

						{userLogged && (
							<div>
								<Tabs className={css.tabs} value={activeMenu}>
									<Box pt={1} pb={1} paddingRight={1.5}>
										<NavLink to={routerUrls.userPlan}>
											<Button
												variant="outlined"
												color="inherit"
												size="small"
												type="submit"
											>
												{LL('common.userHeader.myTariff')}
											</Button>
										</NavLink>
									</Box>
									<LangSwitcher />
									<IconButton
										id="menu-appbar"
										color="inherit"
										onClick={this.handleMenu}
									>
										<AccountCircle />
									</IconButton>

									<Popover
										id={'menu-appbar'}
										className={css.headerMenu}
										anchorEl={anchorEl}
										open={open}
										onClose={this.handleClose}
										anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
										transformOrigin={{horizontal: 'right', vertical: 'top'}}
									>
										<MenuItem
											component={NavLink}
											to={routerUrls.userProfile}
											onClick={this.handleClose}
										>
											<ListItemIcon>
												<AccountBoxIcon />
											</ListItemIcon>

											<ListItemText primary={LL('common.profile')} />
										</MenuItem>

										<MenuItem onClick={this.handleLogout}>
											<ListItemIcon className="">
												<ExitToAppIcon />
											</ListItemIcon>

											<ListItemText primary={LL('common.exit')} />
										</MenuItem>
									</Popover>
								</Tabs>
							</div>
						)}
					</Toolbar>
				</div>
			</AppBar>
		);
	}
}

// @ts-ignore
export default withTracker<IUserHeaderProps, {}>(() => {
	const group = Group.findOne({name: sharedConsts.user.slideShowGroupDefault}) || Group.findOne();

	return {
		defaultGroupId: group?._id,
		userLogged: Boolean(Meteor.userId()),
	};
})(withRouter(UserHeader));
